import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, ProfileContext }  from '../App';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, renderColumnHeader, TableProps, useReactTable } from './Grid';
import Waiting from './Waiting';
import { decrementUnreadAlertCountLabel, setUnreadAlertCountLabel } from '../util/AlertCountHack'
import { formatDate } from '../util/Format';
import { get, putForm } from '../util/HttpRequest'
import { AlertData, AlertsData, PostResponse } from './Models';
import GridSummary from './GridSummary';
import GridPagination from './GridPagination';
import { NavAlertIcon, CaretDownIcon, CaretRightIcon } from './Icons'

function Alerts(props) {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [gridData, setGridData] = useState(new AlertsData());
    const {accessToken, extendSession, user} = useContext(AuthContext);
    const {changeUserProfileLossDraftsClaim, languagePreference, userProfileLossDraftsClaim} = useContext(ProfileContext);
    const { t } = useTranslation(["Alerts", "DataGrid", "Claim"]);
    const claimsNavItems = [
        import.meta.env.VITE_CLAIMS_TAB_TASKS||"",
        import.meta.env.VITE_CLAIMS_TAB_DOCUMENTS||"",
        import.meta.env.VITE_CLAIMS_TAB_TRANSACTIONS||"",
        import.meta.env.VITE_CLAIMS_TAB_INSPECTIONS||"",
        import.meta.env.VITE_CLAIMS_TAB_CONTRACTORS||""
    ];
    const columnHeaderProps = [
        {
            id: 'createdDateUTC', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'consumerAlertTemplateDescription', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        }
    ];

    useEffect (() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languagePreference]);

    function refreshData() {
        setLoading(true);
        getData();
    };

    function markAsRead(consumerAlertIds:string) {
        extendSession();
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/MarkConsumerAlertsAsRead");
        const formData = new FormData();
        formData.append("UserId", user.id);
        formData.append("consumerAlertIds", consumerAlertIds);
        console.log("Marking Consumer Alerts as read to " + url);
        
        putForm(url, accessToken, formData)
        .then((response) => {
            if (!response.ok) { throw new Error(response.status + " - " + response.statusText)}
            return response.json() as Promise<PostResponse>;
        })
    };

    function getData() {
        extendSession();
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ConsumerAlerts");
        url.searchParams.set("UserId", user.id);
        console.log("Fetch Claim ConsumerAlerts from " + url);

        get(url, accessToken)
        .then( response => {
            if (!response.ok) { throw response }
            return response.json() as Promise<AlertData[]>
        })
        .then( searchResults => {
            const data = {
                recordCount: searchResults.length, 
                refreshDate: new Date(),
                searchResults: searchResults
            };
            setGridData(data);
            const unreadConsumerAlertIds = searchResults.filter(x => {return x.isRead === false}).map(x => x.consumerAlertId);
            setUnreadAlertCountLabel(unreadConsumerAlertIds.length);
            setErrorMsg("");
        })
        .catch( error => {
            setGridData(new AlertsData());
            setErrorMsg(error.message);
        })
        .finally(() => {
            setLoading(false);
        })
    };

    function handleClaimLinkClick(claimId: number) {
        if (claimId > 0 && claimId !== userProfileLossDraftsClaim.claimId) {
            changeUserProfileLossDraftsClaim(claimId);
        }
    };

    function handleTableRowExpanderClick(row) {
        // In TanStack Table v8, getIsExpanded() always returns a boolean,
        // so there's no need to check for undefined like in earlier versions.
        if ((!row.getIsExpanded()) && row.original.isRead === false) {
            row.original.isRead = true;
            decrementUnreadAlertCountLabel();
            markAsRead(row.original.consumerAlertId.toString());
        }
        row.toggleExpanded();
    };
    
    function handleTableRowKeyDown (event: React.KeyboardEvent<HTMLTableRowElement>, row) {
        event.stopPropagation();
        switch (event.key) {
          case "Enter": 
            handleTableRowExpanderClick(row);
            break;  
          default: break;
        }
    };

    // A simple way to support a renderRowSubComponent is to make a render prop
    // This is NOT part of the React Table API, it's merely a rendering
    // option we are creating for ourselves in our table renderer
    function Table<TData extends object>({ columns, data }: TableProps<TData>) {
        const pageSize = Number(import.meta.env.VITE_GRID_PAGE_SIZE);
         // Use the state and functions returned from useReactTable to build your UI
        const table = useReactTable({
            data,
            columns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(), //load client-side pagination code
            getSortedRowModel: getSortedRowModel(),
            getExpandedRowModel: getExpandedRowModel(),
            initialState: {
                pagination: {
                    pageIndex: 0,
                    pageSize: pageSize
                },
            }, 
        });

        const pagination = (
            <GridPagination
                canPreviousPage={table.getCanPreviousPage()}
                canNextPage={table.getCanNextPage()}
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                gotoPage={table.setPageIndex}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
            />
        );

        const summary = (
            <GridSummary
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                pageSize={pageSize}
                recordCount={data.length}
                recordType={t('gridRecordType', { count: data.length })}
                refreshDate={gridData.refreshDate}
                pagination={pagination}
            />
        );
        
        return (
            <>
                {summary}
                <table className="table" role="table">
                    <thead role="rowgroup">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} role="row">
                                {headerGroup.headers.map((header) =>
                                    renderColumnHeader(header, columnHeaderProps.find(x => x.id === header.column.id))
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody role="rowgroup">
                        {table.getRowModel().rows.map((row, i) => (
                            <React.Fragment key={row.id}>
                                <tr
                                    className={"row-" + (i % 2 === 0 ? "even" : "odd") + " pointer"}
                                    role="row"
                                    tabIndex={0}
                                    id={row.id}
                                    onKeyDown={(e) => handleTableRowKeyDown(e, row)}
                                    onClick={() => handleTableRowExpanderClick(row)}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} role="cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                                {row.getIsExpanded() && (
                                    <tr className="row-detail" tabIndex={0}>
                                        <td colSpan={row.getVisibleCells().length}>
                                            {renderRowSubComponent({ row })}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </>
        )
    }

    const renderRowSubComponent = React.useCallback(
        ({ row }) => {
            let result = <>{row.original.alertText}</>;
            claimsNavItems.some(item => {
                const itemCamelCase = item.charAt(0).toUpperCase() + item.substring(1);
                const itemLinkSearch = "{" + itemCamelCase + "Link}";
                const itemLinkIndex = row.original.alertText?.search(itemLinkSearch);
                if (itemLinkIndex > -1) {
                    const linkUrl = "/claims/" + itemCamelCase;
                    const linkTitle = t("Claim:menu" + itemCamelCase);
                    const claimId = Number(row.original.keyData);
                    result = <>
                        {
                            row.original.alertText.substring(0, itemLinkIndex)
                        }
                        <NavLink to={linkUrl} className={({ isActive }) => "nav-item" + (isActive ? "-current" : "")} onClick={() => handleClaimLinkClick(claimId)}
                        >
                            {linkTitle}
                        </NavLink>
                        {
                            row.original.alertText.substring(itemLinkIndex + itemLinkSearch.length)
                        }
                    </>;
                    return true;
                }
                return false;
            })
            return result;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    function renderDataGrid() {
        const columns = [{
            id: 'expander',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="datagrid-data-left">
                 {row.getIsExpanded()
                   ? <CaretRightIcon title={t('rowCollapse')} />
                   : <CaretDownIcon title={t('rowExpand')} />
                 }
                </div>
            )
        }, {
            id: 'actions',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="datagrid-data-center">
                    <span className="action-icon">
                        {
                            row.original.isRead === false && (
                                <NavAlertIcon class="medium-icon color-red" title={t('filterUnread')} />
                            )
                        }
                    </span>
                </div>
            )
        }, {
            header: () => (<>{t('columnDate')}</>),
            accessorKey: 'createdDateUTC',
            enableSorting: true,
            cell: info  => (
                <div className="datagrid-data-left">
                {
                    formatDate(info.getValue())
                }
                </div>
            )
        }, {
            header: () => (<>{t('columnAlert')}</>),
            accessorKey: 'consumerAlertTemplateDescription',
            enableSorting: true
        }];

        const table = <Table
            columns={columns}
            data={gridData.searchResults} 
            renderRowSubComponent={renderRowSubComponent}
        />;

        return <div>{table}</div>;
    };

    const title = <h2>{t('title')}</h2>;
    const contents = (errorMsg > '') ? <p><em>{errorMsg}</em></p> : loading ? <Waiting size='large' /> : renderDataGrid();
    return  <div>{title}<br />{contents}</div>;
};

export default Alerts;
